/**
 * LocationMedia — card 222×207: mapa + faixa branca (endereço) + horário no canto.
 */
import React, { useMemo, useState } from 'react';
import { AppIcon } from '../../../../../components/icons/AppIcon';
import { FormatTime } from '../FormatTime';
import { StatusIndicator } from '../StatusIndicator';
import { buildStaticMapUrl } from '../../../lib/googlePlacesSearch';
import { safeParse } from './types.js';
import { MediaFallback } from './MediaFallback.js';

/** Detecta texto que é só lat,lng (ex.: localização atual do WhatsApp sem endereço). */
function looksLikeCoordinates(value?: string | null): boolean {
    if (!value?.trim()) return true;
    return /^-?\d+(?:[.,]\d+)?\s*,\s*-?\d+(?:[.,]\d+)?$/.test(value.trim());
}

interface LocationMediaProps {
    content?: string;
    timestamp?: string | Date;
    status?: string;
    isMe?: boolean;
}

export const LocationMedia: React.FC<LocationMediaProps> = ({
    content,
    timestamp,
    status,
    isMe = false,
}) => {
    const [mapFailed, setMapFailed] = useState(false);
    const data = useMemo(() => safeParse(content), [content]);

    if (!data) return <MediaFallback type="location" fallbackLabel="Localização inválida" />;

    const { degreesLatitude, degreesLongitude, address, name, jpegThumbnail } = data;
    const lat = Number(degreesLatitude);
    const lng = Number(degreesLongitude);
    const mapUrl = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
    // Localização atual do WhatsApp muitas vezes vem sem nome/endereço — não exibir lat/lng cru.
    const title = looksLikeCoordinates(name) ? 'Localização' : (name?.trim() || 'Localização');
    const subtitle = looksLikeCoordinates(address) ? '' : (address?.trim() || '');
    const hasValidThumbnail = typeof jpegThumbnail === 'string' && jpegThumbnail.length > 50;
    const staticMap =
        Number.isFinite(lat) && Number.isFinite(lng) ? buildStaticMapUrl(lat, lng, 222, 150) : '';

    return (
        <article className="chat-rich-card m-1.5 flex h-[207px] w-[222px] max-w-[calc(100%-0.75rem)] flex-col overflow-hidden rounded-xl bg-card shadow-xs">
            <a
                href={mapUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="relative block h-[150px] w-full shrink-0 overflow-hidden bg-surface-subtle no-underline"
                aria-label={`Abrir mapa de ${title}`}
            >
                {hasValidThumbnail ? (
                    <img
                        src={`data:image/jpeg;base64,${jpegThumbnail}`}
                        alt=""
                        className="h-full w-full object-cover"
                    />
                ) : staticMap && !mapFailed ? (
                    <img
                        src={staticMap}
                        alt=""
                        className="h-full w-full object-cover"
                        onError={() => setMapFailed(true)}
                    />
                ) : (
                    <span className="flex h-full w-full items-center justify-center">
                        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-card text-secondary shadow-xs">
                            <AppIcon name="location" className="h-5 w-5" />
                        </span>
                    </span>
                )}
            </a>

            <footer className="relative flex min-h-0 flex-1 flex-col justify-center border-t border-subtle bg-card px-2.5 pb-4 pt-1.5">
                <div className="min-w-0 pr-12">
                    <div className="truncate text-[13px] font-semibold leading-tight text-default">
                        {title}
                    </div>
                    {subtitle ? (
                        <div className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-muted">
                            {subtitle}
                        </div>
                    ) : null}
                </div>

                {timestamp != null && (
                    <div className="absolute bottom-1 right-1.5 flex items-center gap-0.5 text-muted">
                        <FormatTime date={timestamp} />
                        <StatusIndicator status={status} isMe={isMe} variant="muted" />
                    </div>
                )}
            </footer>
        </article>
    );
};
